/*
Not to be confused for comment-macro-extension-handler.tsx in the same package which is used specifically
for the comment editor. This MacroExtensionHandler file is used for creating extension handlers
for next/full-page-editor
*/
import React, { Fragment } from 'react';

import type { ExtensionHandler, ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { EditorActions } from '@atlaskit/editor-core';

import { ExperienceTimeout } from '@confluence/experience-tracker';
import {
	MacroExperienceStart,
	getMacroAttributesFromADFNode,
	EDITOR,
	getExperienceName,
} from '@confluence/macro-tracker';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/extensions-common';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types';
import { fg } from '@confluence/feature-gating';
import { PageContentRenderer } from '@confluence/content-renderer';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features';
import { PagePropertiesReportRenderer } from '@confluence/page-properties-report-renderer';
import { ProfilePictureMacroHandler, UserProfileMacroHandler } from '@confluence/profile-macros';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer';

import { AnchorMacroRenderer } from './AnchorMacroRenderer';
import { DeadMacroRenderer } from './DeadMacroRenderer';
import { CreateFromTemplate } from './CreateFromTemplate';
import { getMigrationExtHandlerForEditor } from './MigrationExtension';
import { ExcerptIncludeExtensionHandler } from './ExcerptInclude';
import { TableOfContentsLiveComponent } from './TableOfContentsLiveComponent';
import { LiveSearchHandler } from './LiveSearch/LiveSearchHandler';

const context = {
	insertCss: (styles) => styles._insertCss(),
};

type Options = {
	contentId: string;
	spaceKey: string;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	editorActions?: EditorActions;
	subscribeDocChange: SubscriptionHandlerFnType;
	unsubscribeDocChange: SubscriptionHandlerFnType;
	isLivePage?: boolean;
};

export const getFullPageExtensionHandlers = ({
	contentId,
	spaceKey,
	extensionContextProvider,
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
}: Options): ExtensionHandlers => {
	return {
		[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandlerForEditor({
			contentId,
			spaceKey,
			extensionContextProvider,
			editorActions,
			subscribeDocChange,
			unsubscribeDocChange,
			isLivePage,
		}),
		[EXTENSION_TYPE.MIGRATION]: getMigrationExtHandlerForEditor({
			contentId,
			contextProviders: {}, // we do not yet need ClassicEditorContextProvider for InvalidExtension in next editor
		}),
	};
};

export const getMacroExtensionHandlerForEditor = ({
	contentId,
	spaceKey,
	extensionContextProvider = {
		ClassicEditorContextProvider: ({
			children,
			// @ts-ignore
			isWithinEditor,
		}: {
			children: React.ReactNode;
			isWithinEditor?: boolean;
		}) => <>{children}</>,
	},
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
}): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension, doc) => {
		const { extensionKey } = extension;
		const attributes = getMacroAttributesFromADFNode(extension);
		const { ClassicEditorContextProvider } = extensionContextProvider;

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			references: doc?.[0]?.references || [],
			contentId,
			spaceKey,
			mode: EDITOR,
			context,
			extensionKey,
			attributes,
			multiBodiedExtensionActions: doc?.[0]?.actions,
		};

		let implementation;

		const legacyMacroRenderer = (
			<ClassicEditorContextProvider>
				<LegacyMacroRenderer {...macroDefaultProps} />
			</ClassicEditorContextProvider>
		);

		switch (extensionKey) {
			// blocklist - block extensions - we want to show the editor's placeholder frame, so return empty div
			case 'toc': // toc needs to be rewritten as it accesses the DOM to create its tree
				implementation = (
					<TableOfContentsLiveComponent
						macroDefaultProps={macroDefaultProps}
						editorActions={editorActions}
						subscribeDocChange={subscribeDocChange}
						unsubscribeDocChange={unsubscribeDocChange}
					/>
				);
				break;

			case 'create-from-template':
				implementation = <CreateFromTemplate {...macroDefaultProps} />;
				break;

			// blocklist - inline extensions - the editor doesn't render a placeholder frame, so we need to display something
			case 'pagetree': // pagetree is not spa friendly and will only work sometimes
				implementation = <DeadMacroRenderer {...macroDefaultProps} />;
				break;

			case 'anchor':
				implementation = <AnchorMacroRenderer {...macroDefaultProps} />;
				break;

			case 'excerpt-include':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<ExcerptIncludeExtensionHandler
							{...macroDefaultProps}
							isLivePage={isLivePage}
							Renderer={PageContentRenderer}
						/>
					</ClassicEditorContextProvider>
				);
				break;

			case 'detailssummary':
				implementation = fg('confluence_ppr_adf_rendering') ? (
					<ClassicEditorContextProvider isWithinEditor>
						<PagePropertiesReportRenderer
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
							openAnchorsInNewTab
						/>
					</ClassicEditorContextProvider>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'profile-picture':
				implementation = (
					<ProfilePictureMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'profile':
				implementation = (
					<UserProfileMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'include':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<LegacyMacroRenderer {...macroDefaultProps} />
					</ClassicEditorContextProvider>
				);
				break;
			case 'livesearch':
				implementation = <LiveSearchHandler macroDefaultProps={macroDefaultProps} />;
				break;

			// generic macro renderer
			default:
				implementation = legacyMacroRenderer;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={contentId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
